import React from 'react'
import { gql } from '@apollo/client'
import map from 'lodash/map'
import type { PurchaseOrder, RouteDef } from '@ztechapps/common.contract'
import { useDocument } from '@ztechapps/common.hook'
import { useInject, WebuiIocKey } from '@ztechapps/webui.contract'
import { LocalDate } from '@ztechapps/webui.date'
import { getCompanyOrProfileName } from '@ztechapps/common.utils'
import { EntityCodeLink, EntityPreview, ShowIf } from '@ztechapps/webui.util'
import { PurchaseOrderStatus, PurchaseOrderStatusBadge } from '../PurchaseOrderStatus/PurchaseOrderStatusBadge'

const purchaseOrderPreviewQuery = gql`
  query singlePurchaseOrder($filter: JSON) {
    singlePurchaseOrder(filter: $filter) {
      _id
      code
      status
      purchaseDate
      estimatedArrivalDate
      vendor {
        _id
        profile {
          _id
          name
        }
        company {
          _id
          name
        }
      }
      shipTo {
        _id
        profile {
          _id
          name
        }
        company {
          _id
          name
        }
      }
      items {
        _id
        product {
          _id
          code
          name
        }
        quantity
        uom {
          _id
          symbol
        }
      }
    }
  }
`

const PurchaseOrderPreview = ({ _id, code }: { _id: string; code: string }) => {
  const purchaseOrderDocumentResult = useDocument<PurchaseOrder>({ _id, query: purchaseOrderPreviewQuery })
  const purchaseOrder = purchaseOrderDocumentResult.document

  return (
    <EntityPreview
      code={code}
      statusBadge={<PurchaseOrderStatusBadge status={purchaseOrder?.status} />}
      loading={purchaseOrderDocumentResult.loading}
      title={getCompanyOrProfileName(purchaseOrder?.vendor)}
      rows={[
        { label: 'Ship To', value: getCompanyOrProfileName(purchaseOrder?.shipTo) },
        // `LocalDate` always renders SOMETHING (its own "--" for a missing date), so
        // `EntityPreview`'s `?? '---'` fallback never sees a falsy value to catch — checked here
        // instead, same as `WorkOrderCodeLink`'s Tags row.
        {
          label: 'Purchase Date',
          value: purchaseOrder?.purchaseDate ? <LocalDate value={purchaseOrder?.purchaseDate} /> : undefined,
        },
        {
          label: 'Estimated Arrival',
          value: purchaseOrder?.estimatedArrivalDate ? (
            <LocalDate value={purchaseOrder?.estimatedArrivalDate} />
          ) : undefined,
        },
      ]}
      list={{
        label: 'Items',
        items: map(purchaseOrder?.items, (item) => (
          <>
            {item?.product?.code} - {item?.product?.name} [{item?.quantity} {item?.uom?.symbol}]
          </>
        )),
      }}
    />
  )
}

export type PurchaseOrderCodeLinkProps = { _id: string; code: string; status?: string }

// `status` is optional — mirrors `OrderCodeLink`'s shape (`libs/order/webui/src/OrderCodeLink/OrderCodeLink.tsx`):
// a caller that only has `_id`/`code` on hand (e.g. the communication inbox reference, which fetches a
// lighter query with no status) still gets the code link, just without the status dot; the hover preview
// fetches the full document — status included — on its own.
export const PurchaseOrderCodeLink = ({ _id, code, status }: PurchaseOrderCodeLinkProps) => {
  const purchaseOrderItemRoute = useInject<RouteDef>(WebuiIocKey.routes.purchaseOrderItem)

  return (
    <EntityCodeLink
      code={code}
      statusDot={
        <ShowIf condition={!!status}>
          <PurchaseOrderStatus status={status} />
        </ShowIf>
      }
      to={purchaseOrderItemRoute.altPath({ _id })}
      target="_blank"
      preview={<PurchaseOrderPreview _id={_id} code={code} />}
    />
  )
}
